import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
import { AddCircle, CloudUpload } from "@mui/icons-material";
import { Box, useTheme, alpha, Grid, Typography } from "@mui/material";
import { createContext, useEffect, useRef, useState, } from "react";
import { useTranslation } from "react-i18next";
import nerdUII18n from "../i18n";
const NerdFileUploadContext = createContext({
    nerdFileUploadCallbacks: new Map(),
    // eslint-disable-next-line @typescript-eslint/no-unused-vars
    subscribe: (_id, _callback) => {
        //
    },
    // eslint-disable-next-line @typescript-eslint/no-unused-vars
    unsubscribe: (_id) => {
        //
    },
});
const NerdFileUploadContextProvider = NerdFileUploadContext.Provider;
export { NerdFileUploadContext };
/**
 *
 * @param {DropFildProps} props DropField props
 * @returns {ReactElement} DropField
 */
function DropField(props) {
    const { callback, label, dropZoneSx, dropZoneIcon } = props;
    const [hover, setHover] = useState(false);
    const theme = useTheme();
    return (_jsx(Grid, { item: true, xs: 3, onDrop: (e) => {
            callback(e);
            setHover(false);
        }, onDragOver: () => setHover(true), onDragLeave: () => setHover(false), children: _jsxs(Grid, { container: true, justifyContent: "center", alignItems: "center", padding: 1, sx: {
                border: "2px dashed",
                borderColor: hover ? "green" : theme.palette.primary.main,
                borderRadius: 4,
                ...dropZoneSx,
            }, children: [_jsx(Grid, { item: true, xs: 12, textAlign: "center", children: _jsx(Typography, { variant: "h3", children: dropZoneIcon ? (dropZoneIcon) : (_jsx(AddCircle, { fontSize: "inherit", color: hover ? "success" : "primary" })) }) }), _jsx(Grid, { item: true, textAlign: "center", children: _jsx(Typography, { variant: "h5", children: label || "" }) })] }) }));
}
/**
 *
 * @param {PropsWithChildren<NerdFileUploadWrapperProps>} props NerdFileUploadWrapper props
 * @returns {ReactElement} NerdFileUploadWrapper
 */
export default function NerdFileUploadWrapper(props) {
    const { dropZoneSx, wrapperSx, dropZoneIcon } = props;
    const { t } = useTranslation("components", { i18n: nerdUII18n });
    const [open, setOpen] = useState(false);
    const [nerdFileUploadCallbacks, setNerdFileUploadCallbacks] = useState(new Map());
    const theme = useTheme();
    const dropZoneRef = useRef();
    const [dropAreas, setDropAreas] = useState([]);
    const unsubscribe = (id) => {
        setNerdFileUploadCallbacks((oldState) => {
            const newMap = new Map(oldState);
            newMap.delete(id);
            return newMap;
        });
        setDropAreas((oldState) => oldState.filter((area) => area.key != id));
    };
    const subscribe = (id, callback, label) => {
        setNerdFileUploadCallbacks((oldState) => {
            const newMap = new Map(oldState);
            newMap.set(id, { callback, label });
            return newMap;
        });
        setDropAreas((oldState) => {
            return [
                ...oldState,
                _jsx(DropField, { callback: callback, label: label, dropZoneSx: dropZoneSx, dropZoneIcon: dropZoneIcon }, id),
            ];
        });
    };
    const contextState = {
        nerdFileUploadCallbacks,
        subscribe,
        unsubscribe,
    };
    useEffect(() => {
        if (nerdFileUploadCallbacks.size === 0) {
            setOpen(false);
        }
    }, [nerdFileUploadCallbacks]);
    return (_jsx(NerdFileUploadContextProvider, { value: contextState, children: _jsxs(Box, { sx: {
                width: "100%",
                height: "100%",
                position: "relative",
            }, onDragOver: (e) => {
                if (nerdFileUploadCallbacks.size > 0) {
                    e.preventDefault();
                    setOpen(true);
                }
            }, children: [nerdFileUploadCallbacks.size > 0 ? (_jsx(Box, { ref: dropZoneRef, sx: {
                        position: "absolute",
                        width: "100%",
                        height: "100%",
                        borderRadius: "4px",
                        border: `2px dashed ${theme.palette.primary.main}`,
                        backgroundColor: open
                            ? alpha(theme.palette.primary.main, 0.2)
                            : "inherit",
                        zIndex: theme.zIndex.modal + 1,
                        display: open ? "block" : "none",
                        ...wrapperSx,
                    }, onDragLeave: (e) => {
                        if (!(e.relatedTarget instanceof Node &&
                            dropZoneRef &&
                            dropZoneRef.current?.contains(e.relatedTarget))) {
                            setOpen(false);
                        }
                    }, onDrop: (e) => {
                        e.preventDefault();
                        setOpen(false);
                        if (nerdFileUploadCallbacks.size === 1) {
                            nerdFileUploadCallbacks.forEach((value) => value.callback(e));
                        }
                    }, children: _jsxs(Grid, { container: true, sx: {
                            width: "100%",
                            height: "100%",
                            backdropFilter: "blur(10px)",
                        }, alignItems: "center", children: [_jsxs(Grid, { item: true, xs: 12, textAlign: "center", children: [_jsx(Typography, { variant: "h2", children: _jsx(CloudUpload, { fontSize: "inherit" }) }), _jsx(Typography, { variant: "h4", children: nerdFileUploadCallbacks.size > 1
                                            ? t("nerdFileUploadWrapper.multiple_fields")
                                            : t("nerdFileUploadWrapper.single_field") })] }), dropAreas.length > 1 ? (_jsx(Grid, { item: true, xs: 12, p: 4, children: _jsx(Grid, { container: true, alignItems: "center", justifyContent: "center", spacing: 1, children: dropAreas }) })) : null] }) })) : null, _jsx(Box, { sx: {
                        display: "flex",
                        flex: "1",
                        minHeight: "100%",
                    }, children: props.children })] }) }));
}
